import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { Pagination } from '../models/pagination.model';
import { WithOptional } from 'app/shared/types';

export abstract class PaginationService<T> {
  abstract updateItems(items: T[]): void;
  abstract setPageSize(size: number): void;
  abstract getTotalPages(): Observable<number>;
  abstract getCurrentPage(): Observable<number>;
  abstract getPaginatedItems(): Observable<T[]>;
  abstract getTotalRecords(): Observable<number>;
  abstract connect(): Observable<T[]>;
  abstract nextPage(): void;
  abstract prevPage(): void;
  abstract goToPage(page: number): void;
  abstract reset(): void;
  abstract getPageSize(): Observable<number>;
}

export class PaginationServiceInMemory<T> implements PaginationService<T> {
  private items$ = new BehaviorSubject<T[]>([]);
  private pageSize$ = new BehaviorSubject<number>(10);
  private currentPage$ = new BehaviorSubject<number>(1);
  private destroyRef;

  constructor(
    pagination: WithOptional<Pagination<T>, 'totalItems' | 'totalPages'>,
    destroyRef?: DestroyRef,
  ) {
    this.items$.next(pagination.items);
    this.pageSize$.next(pagination.offset);
    this.currentPage$.next(pagination.currentPage);
    this.destroyRef = destroyRef;
  }

  /** Atualiza os itens na memória */
  updateItems(items: T[]): void {
    this.items$.next(items);
    this.currentPage$.next(1);
  }

  /** Define o tamanho da página */
  setPageSize(size: number): void {
    if (size < 1) throw new Error('Page size must be at least 1');
    this.pageSize$.next(size);
    this.currentPage$.next(1);
  }

  getPageSize(): Observable<number> {
    return this.pageSize$.asObservable();
  }

  /** Retorna o total de páginas */
  getTotalPages(): Observable<number> {
    return this.items$.pipe(
      map(items => Math.ceil(items.length / this.pageSize$.value)),
    );
  }

  /** Retorna a página atual */
  getCurrentPage(): Observable<number> {
    return this.currentPage$.asObservable();
  }

  /** Retorna os itens paginados */
  getPaginatedItems(): Observable<T[]> {
    return combineLatest([this.items$, this.pageSize$, this.currentPage$]).pipe(
      map(([items, pageSize, currentPage]) => {
        const start = (currentPage - 1) * pageSize;
        const itemsSliced = items.slice(start, start + pageSize);
        return itemsSliced;
      }),
    );
  }

  /** Retorna a quantidade total dos itens */
  getTotalRecords(): Observable<number> {
    return this.items$.asObservable().pipe(map(items => items.length));
  }

  /** Conecta os dados para uso em tabelas */
  connect(): Observable<T[]> {
    return this.getPaginatedItems();
  }

  /** Paginação */
  nextPage(): void {
    this.getTotalPages()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(totalPages => {
          if (this.currentPage$.value < totalPages) {
            this.currentPage$.next(this.currentPage$.value + 1);
          }
        }),
      )
      .subscribe();
  }

  prevPage(): void {
    if (this.currentPage$.value > 1) {
      this.currentPage$.next(this.currentPage$.value - 1);
    }
  }

  goToPage(page: number): void {
    this.getTotalPages()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(totalPages => {
          if (page >= 1 && page <= totalPages) {
            this.currentPage$.next(page);
          } else {
            throw new Error('Invalid page number');
          }
        }),
      )
      .subscribe();
  }

  reset(): void {
    this.currentPage$.next(1);
  }
}
